import Link from 'next/link';
import { Box, LinearProgress, SxProps, Theme, Typography, useTheme } from '@mui/material';

import { TextAvatar } from '@/components';
import { colors } from '@/styles/theme';
import { EventMarket } from '@/types';
import { clamp, formatProbability, getStableColor, resolveOutcomeData } from '@/utils';

import { ROUTES } from '@/constants';

interface SportsFuturesRowProps {
  market: EventMarket;
  eventSlug: string;
  isLeading?: boolean;
  showLinearProgress?: boolean;
  itemNameSx?: SxProps<Theme>;
  percentSx?: SxProps<Theme>;
}

const transformMarketData = (market: EventMarket) => {
  const { name, price } = resolveOutcomeData(market);

  return {
    itemName: name,
    displayPercent: `${formatProbability(price, 0)}%`,
    progressValue: clamp(price * 100, 0, 100),
    stableColor: getStableColor(market.id),
  };
};

export const SportsFuturesRow: React.FC<SportsFuturesRowProps> = ({
  market,
  eventSlug,
  isLeading = false,
  showLinearProgress = true,
  itemNameSx = {
    fontWeight: 700,
    fontSize: '0.9375rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'text.primary',
    width: '12.5rem',
    flexShrink: 0,
  },
  percentSx = {
    fontWeight: 800,
    fontSize: '1.2rem',
    textAlign: 'right',
    color: 'text.primary',
    lineHeight: 1,
  },
}) => {
  const theme = useTheme();

  const { itemName, displayPercent, progressValue, stableColor } = transformMarketData(market);
  const isPercentHidden = displayPercent === '0%';
  const shouldShowBar = showLinearProgress && !isPercentHidden;

  return (
    <Link href={ROUTES.MARKET_DETAILS(eventSlug)} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '0.75rem 0',
          gap: '1rem',
          '&:hover .market-name': {
            textDecoration: 'underline',
          },
        }}
      >
        <TextAvatar text={itemName} size={32} color={stableColor} />

        <Typography variant="body2" className="market-name" sx={itemNameSx}>
          {itemName}
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            minWidth: 0,
          }}
        >
          <Typography variant="caption" sx={percentSx}>
            {displayPercent}
          </Typography>
          {shouldShowBar && (
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: '2.25rem',
                borderRadius: 1,
                flexGrow: 1,
                backgroundColor: theme.palette.mode === 'dark' ? colors.bg.secondary.dark : '#ffffffff',
                opacity: isLeading ? 1 : 0.8,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 1,
                  backgroundColor: stableColor,
                  backgroundImage: `linear-gradient(90deg, ${stableColor} 0%, ${stableColor}cc 100%)`,
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Link>
  );
};
